import React, { useEffect, useRef, useState } from 'react';
import { brandsOpenApi, TOP_INDENTS } from '@constants';
import { useStore } from '@hooks';
import { Icon, Typography } from '@ui';
import { axiosBoa } from '@utils';
import { Checkbox, Flex, notification } from 'antd';
import { observer } from 'mobx-react';
import { SETUP_KEYS } from '../../constants';
import {
    DomainInputContainer,
    DomainTagsContainer,
    PlaceholderText,
    StyledDomainInput,
    StyledDomainTag,
    StyledSaveButton
} from './styled';

const COLLAPSE_TO_SETTING_KEY_MAP = {
    [SETUP_KEYS.ANALYTICS_SCRIPT]: 'hasThirdPartyDomain',
    [SETUP_KEYS.PURCHASE_EVENT]: 'hasThirdPartyCheckout'
};

const SubdomainCollapse = observer(({ openedCollapse, initialDomains = [] }) => {
    const store = useStore();
    const { updateAccount, getSetupInfo } = store.accountsStore;
    const currentAccount = store.accountsStore.currentAccount;
    const settings = currentAccount?.settings || {};

    const settingKey = COLLAPSE_TO_SETTING_KEY_MAP[openedCollapse];
    if (!settingKey) {
        return null;
    }

    const initialDomainStrings = initialDomains.map((item) => (typeof item === 'string' ? item : item.domain));
    const initialDomainsKey = initialDomains
        .map((item) => (typeof item === 'string' ? item : `${item.domainId ?? ''}:${item.domain ?? ''}`))
        .join('|');

    const [subDomain, setSubDomain] = useState(settings[settingKey] || false);
    const [domains, setDomains] = useState(initialDomainStrings);
    const [savedDomains, setSavedDomains] = useState(initialDomainStrings);
    const [inputValue, setInputValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const inputRef = useRef(null);
    const savedDomainsRef = useRef(savedDomains);

    const isPurchaseEvent = openedCollapse === SETUP_KEYS.PURCHASE_EVENT;
    const isAnalyticsScript = openedCollapse === SETUP_KEYS.ANALYTICS_SCRIPT;

    const toHostname = (value) => {
        try {
            const urlToCheck = value.includes('://') ? value : `https://${value}`;
            return new URL(urlToCheck).hostname.toLowerCase();
        } catch {
            return null;
        }
    };

    const isValidUrl = (value) => {
        const hostname = toHostname(value);
        if (!hostname) return false;
        const domainPattern =
            /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,6}$/;
        return domainPattern.test(hostname);
    };

    const isDirty =
        domains.length !== savedDomains.length || domains.some((domain, index) => domain !== savedDomains[index]);

    const currentSetting = currentAccount?.settings ? currentAccount.settings[settingKey] : false;

    useEffect(() => {
        setSubDomain(Boolean(currentSetting));
    }, [currentSetting, settingKey]);

    useEffect(() => {
        savedDomainsRef.current = savedDomains;
    }, [savedDomains]);

    useEffect(() => {
        const newSavedStrings = initialDomains.map((item) => (typeof item === 'string' ? item : item.domain));
        const newSavedNormalized = new Set(newSavedStrings.map((d) => (toHostname(d) || d || '').toLowerCase()));

        setDomains((prevDomains) => {
            const prevSavedNormalized = new Set(
                savedDomainsRef.current.map((d) => (toHostname(d) || d || '').toLowerCase())
            );
            const unsavedDomains = prevDomains.filter((d) => {
                const normalized = (toHostname(d) || d || '').toLowerCase();
                return !prevSavedNormalized.has(normalized) && !newSavedNormalized.has(normalized);
            });
            return [...newSavedStrings, ...unsavedDomains];
        });

        setSavedDomains(newSavedStrings);
    }, [initialDomainsKey]);

    const getDomainIdForDomain = (domain) => {
        const key = String(domain).toLowerCase();
        const match = initialDomains.find(
            (item) => typeof item === 'object' && item && String(item.domain).toLowerCase() === key
        );
        return match?.domainId;
    };

    const handleSubDomainChange = async (e) => {
        const newValue = e.target.checked;
        setSubDomain(newValue);

        try {
            await updateAccount(
                {
                    settings: {
                        [settingKey]: newValue
                    }
                },
                'json'
            );
        } catch (error) {
            setSubDomain(!newValue);
        }
    };

    const handleInputChange = (e) => {
        const { value } = e.target;

        if (value.includes(',')) {
            addDomains(value);
        } else {
            setInputValue(value);
        }
    };

    const handleKeyPress = (e) => {
        if (e.code === 'Enter' && !!inputValue) {
            e.preventDefault();
            addDomains(inputValue);
        }
    };

    const addDomains = (value) => {
        const newDomains = value
            .split(',')
            .map((domain) => domain.trim())
            .filter((domain) => domain);

        const normalizedNewDomains = newDomains.map((d) => toHostname(d) || d);
        const updatedDomains = [...domains, ...normalizedNewDomains];
        setDomains(updatedDomains);
        setInputValue('');
    };

    const handleDomainClose = async (removedDomain, index) => {
        const normalizedRemoved = (toHostname(removedDomain) || removedDomain || '').toLowerCase();
        const occurrencesInDomains = domains.filter(
            (d) => ((toHostname(d) || d || '') + '').toLowerCase() === normalizedRemoved
        ).length;

        // If duplicates exist in the UI, never trigger backend delete from the "X" click.
        // Only delete from server when this domain appears once in the current list.
        const isDomainSaved =
            savedDomains.some((d) => ((toHostname(d) || d || '') + '').toLowerCase() === normalizedRemoved) &&
            occurrencesInDomains === 1;

        if (isDomainSaved) {
            setDeleting(true);
            try {
                let domainId = getDomainIdForDomain(removedDomain);
                if (!domainId) {
                    await getSetupInfo?.();
                    domainId = getDomainIdForDomain(removedDomain);
                }

                if (!domainId) {
                    notification.error({ message: 'Failed to delete domain' });
                    return;
                }

                await axiosBoa.delete(brandsOpenApi.setup.domain.delete, {
                    data: { domainId }
                });
                const savedIndex = savedDomains.indexOf(removedDomain);
                const updatedSavedDomains = [...savedDomains];
                updatedSavedDomains.splice(savedIndex, 1);

                const updatedDomains = [...domains];
                updatedDomains.splice(index, 1);
                setDomains(updatedDomains);
                setSavedDomains(updatedSavedDomains);
                notification.success({
                    message: 'Domain deleted successfully'
                });
                await getSetupInfo?.();
            } catch (error) {
                notification.error({
                    message: error?.response?.data?.message || 'Failed to delete domain'
                });
            } finally {
                setDeleting(false);
            }
        } else {
            const updatedDomains = [...domains];
            updatedDomains.splice(index, 1);
            setDomains(updatedDomains);
        }
    };

    const handleSave = async () => {
        const parsed = domains.map((d) => ({ raw: d, hostname: toHostname(d) }));
        const hostnames = parsed.map((p) => p.hostname).filter(Boolean);

        const duplicates = hostnames.filter((h, idx) => hostnames.indexOf(h) !== idx);
        if (duplicates.length > 0) {
            const uniqueDuplicates = [...new Set(duplicates)];
            const label = uniqueDuplicates.length === 1 ? 'Duplicate sub-domain' : 'Duplicate sub-domains';
            notification.error({
                message: `${label}: ${uniqueDuplicates.join(', ')}`
            });
            return;
        }

        const invalidDomains = parsed.filter((p) => !p.hostname || !isValidUrl(p.raw)).map((p) => p.raw);
        if (invalidDomains.length > 0) {
            const label = invalidDomains.length === 1 ? 'sub-domain' : 'sub-domains';
            notification.error({
                message: `Invalid ${label}: ${invalidDomains.join(', ')}`
            });
            return;
        }

        setSaving(true);
        try {
            await axiosBoa.post(brandsOpenApi.setup.domain.create, {
                domains: hostnames
            });

            setDomains(hostnames);
            setSavedDomains(hostnames);
            await getSetupInfo?.();
            notification.success({
                message: 'Domains saved successfully'
            });
        } catch (error) {
            notification.error({
                message: error?.response?.data?.message || 'Failed to save domains'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <Checkbox
                data-e2e={
                    isPurchaseEvent
                        ? 'integrations_purchase-script-third-party_checkbox'
                        : 'integrations_analytics-script-third-party_checkbox'
                }
                checked={subDomain}
                onChange={handleSubDomainChange}
            >
                <Typography.Text type='descriptor1'>
                    {isPurchaseEvent
                        ? 'I have a third-party or custom checkout'
                        : 'I have third-party landing pages or other pages hosted on a sub-domain or different domain'}
                </Typography.Text>
            </Checkbox>
            {subDomain && isAnalyticsScript && (
                <Flex gap={10} align='center' style={TOP_INDENTS.M}>
                    <DomainInputContainer>
                        <PlaceholderText>Pages hosted on a sub-domain or different domain</PlaceholderText>
                        <DomainTagsContainer onClick={() => inputRef.current?.focus()}>
                            {domains.map((domain, index) => (
                                <StyledDomainTag
                                    key={index}
                                    closable
                                    onClose={() => handleDomainClose(domain, index)}
                                    data-e2e={`integrations_domain-${index}_tag`}
                                >
                                    {domain}
                                </StyledDomainTag>
                            ))}
                            <StyledDomainInput
                                ref={inputRef}
                                value={inputValue}
                                onChange={handleInputChange}
                                onKeyPress={handleKeyPress}
                                data-e2e='integrations_domains_input'
                            />
                        </DomainTagsContainer>
                    </DomainInputContainer>
                    <StyledSaveButton
                        type='primary'
                        onClick={handleSave}
                        loading={saving}
                        disabled={!isDirty || deleting}
                        iconRight={<Icon size='16px' component={() => <Icon.CheckIcon />} />}
                        data-e2e='integrations_apply-list_btn'
                    >
                        Apply list
                    </StyledSaveButton>
                </Flex>
            )}
        </div>
    );
});

export default SubdomainCollapse;
